import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getMe, logout as apiLogout, getStoredUser, isAuthenticated } from '../services/authApi';
import { setOnUnauthorized } from '../services/workerClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(true);

  // On mount: validate session if token exists
  useEffect(() => {
    if (!isAuthenticated()) {
      setLoading(false);
      return;
    }
    getMe()
      .then((data) => {
        const userData = { userId: data.userId, email: data.email, role: data.role || 'user' };
        setUser(userData);
        sessionStorage.setItem('sm_user', JSON.stringify(userData));
      })
      .catch(() => {
        // Session expired or invalid → clear
        sessionStorage.removeItem('sm_session_token');
        sessionStorage.removeItem('sm_user');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const onLoginSuccess = useCallback((userData) => {
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
  }, []);

  // Wire up workerClient to auto-logout on 401 (session expired)
  useEffect(() => {
    setOnUnauthorized(() => {
      sessionStorage.removeItem('sm_session_token');
      sessionStorage.removeItem('sm_user');
      setUser(null);
    });
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    onLoginSuccess,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
